import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import validators from '../../../../components/forms/validation/validators';

// Redux
import { useDispatch } from 'react-redux';
import { removePopup, setPopup } from '../../../../redux/actions/popup';
import { addNotification } from '../../../../redux/actions/notification';

// Helpers
import { generateUrl, generateUrlDetail } from '../../../../helpers/request-builder';

// Types & Enums
import { ButtonThemes } from '../../../../enums/button-themes';
import { NotificationMessageType } from '../../../../enums/notification-types';
import { FieldValues } from 'react-hook-form';
import type { ImpressionModel } from '../manage-impression-modelling';

// Components
import ConfigTable from '../../../../components/tables/config-table/config-table';
import Form from '../../../../components/forms/form';

const StyledSection = styled.div``;

const StyledH2 = styled.h2`
    font-size: 1.1rem;
`;

const StyledFormContainer = styled.div`
    background-color: ${props => props.theme.colours.white};
    padding: 20px;
    margin-top: 20px;
`;

type LayoutImpressionModelsProps = {
    data: ImpressionModel[];
    onDelete?: () => void;
    onEdit?: () => void;
    isRefreshing?: boolean;
};

type LocalImpressionModel = ImpressionModel & { selected: boolean };

const LayoutImpressionModels: React.FC<LayoutImpressionModelsProps> = ({ data, onDelete, onEdit, isRefreshing }) => {
    const dispatch = useDispatch();
    const [impressionModels, setImpressionModels] = useState<LocalImpressionModel[]>(
        data.map(m => ({ ...m, selected: false }))
    );
    const [modelsToDelete, setModelsToDelete] = useState<number[]>([]);
    const [modelToEdit, setModelToEdit] = useState<LocalImpressionModel>();
    const [showEditForm, setShowEditForm] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isPatching, setIsPatching] = useState(false);
    const [isPosting, setIsPosting] = useState(false);

    useEffect(() => {
        setImpressionModels(data.map(m => ({ ...m, selected: false })));
    }, [data]);

    const handleDeleteModels = (ids: number[]) => {
        setIsDeleting(true);
        const deleteRequests = ids.map(id => {
            const url = generateUrlDetail('config', 'impression-model', id.toString());
            if (!url) throw new Error('Invalid URL');
            return axios({
                method: 'DELETE',
                url,
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            });
        });

        Promise.all(deleteRequests)
            .then(() => {
                setModelsToDelete([]);
                setImpressionModels(
                    impressionModels.map(model => ({
                        ...model,
                        selected: false,
                    }))
                );
                dispatch(
                    addNotification({
                        copy: 'Impression source successfully deleted.',
                        type: NotificationMessageType.Success,
                    })
                );
                if (onDelete) onDelete();
            })
            .catch(() => {
                setModelsToDelete([]);
                dispatch(
                    addNotification({
                        copy: 'There was an issue while deleting. Please try again later or contact support@cubed.email.',
                        type: NotificationMessageType.Error,
                    })
                );
            })
            .finally(() => setIsDeleting(false));
    };

    const handlePatchModel = (payload: Record<string, any>[], resourceIds: string[]) => {
        setIsPatching(true);
        const patchRequests = resourceIds.map((id, index) => {
            const url = generateUrlDetail('config', 'impression-model', id);
            if (!url) throw new Error('Invalid URL for PATCH');
            return axios({
                method: 'PATCH',
                url,
                data: payload[index],
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            });
        });

        Promise.all(patchRequests)
            .then(() => {
                setShowEditForm(false);
                dispatch(
                    addNotification({
                        copy: 'Impression source successfully updated.',
                        type: NotificationMessageType.Success,
                    })
                );
                if (onEdit) onEdit();
            })
            .catch(() => {
                dispatch(
                    addNotification({
                        copy: 'There was an issue while updating. Please try again later or contact support@cubed.email.',
                        type: NotificationMessageType.Error,
                    })
                );
            })
            .finally(() => setIsPatching(false));
    };

    const handlePostModel = (source: string) => {
        // Prevent adding duplicate sources (case-insensitive)
        if (impressionModels.some(m => m.value.trim().toLowerCase() === source.trim().toLowerCase())) {
            dispatch(
                addNotification({
                    copy: 'This impression source already exists.',
                    type: NotificationMessageType.Error,
                })
            );
            return;
        }
        setIsPosting(true);
        axios({
            method: 'POST',
            url: generateUrl('config', 'impression-model'),
            data: { source },
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
        })
            .then(() => {
                setShowAddForm(false);
                dispatch(
                    addNotification({
                        copy: 'Impression source successfully added.',
                        type: NotificationMessageType.Success,
                    })
                );
                if (onEdit) onEdit();
            })
            .catch(error => {
                const isDuplicate =
                    error?.response?.data?.error_message?.includes('Duplicate entry') ||
                    error?.response?.data?.message?.includes('Duplicate entry');
                dispatch(
                    addNotification({
                        copy: isDuplicate
                            ? 'This impression source already exists.'
                            : 'There was an issue while adding. Please try again later or contact support@cubed.email.',
                        type: NotificationMessageType.Error,
                    })
                );
            })
            .finally(() => setIsPosting(false));
    };

    const handleCheckboxChange = (rowId: number) => {
        const updatedModels = impressionModels.map(model => {
            if (model.id === rowId) {
                return { ...model, selected: !model.selected };
            }
            return model;
        });
        setImpressionModels(updatedModels);
        setModelsToDelete(updatedModels.filter(m => m.selected).map(m => m.id));
    };

    const handleSingleDeleteClick = (rowId: number) => {
        setModelsToDelete([rowId]);

        const handleDiscardClick = () => {
            setModelsToDelete([]);
            dispatch(removePopup());
        };

        const handleDelete = () => {
            handleDeleteModels([rowId]);
            dispatch(removePopup());
        };

        dispatch(
            setPopup({
                title: 'Delete',
                iconType: 'warning',
                contentType: 'simple',
                config: {
                    copy: 'Are you sure you would like to delete this impression source? This will also delete any associated configurations.',
                },
                buttons: [
                    {
                        onClick: handleDelete,
                        value: 'YES, DELETE',
                    },
                    {
                        onClick: handleDiscardClick,
                        value: 'CANCEL',
                        buttonTheme: ButtonThemes.Secondary,
                    },
                ],
            })
        );
    };

    const handleBulkDeleteClick = () => {
        const handleDiscardClick = () => {
            dispatch(removePopup());
        };

        const handleDelete = () => {
            handleDeleteModels(modelsToDelete);
            dispatch(removePopup());
        };

        dispatch(
            setPopup({
                title: 'Delete',
                iconType: 'warning',
                contentType: 'simple',
                config: {
                    copy: 'Are you sure you would like to delete the selected impression sources? This will also delete any associated configurations.',
                },
                buttons: [
                    {
                        onClick: handleDelete,
                        value: 'YES, DELETE',
                    },
                    {
                        onClick: handleDiscardClick,
                        value: 'CANCEL',
                        buttonTheme: ButtonThemes.Secondary,
                    },
                ],
            })
        );
    };

    const handleEditClick = (rowId: number) => {
        const model = impressionModels.find(m => m.id === rowId);
        if (model) {
            setModelToEdit(model);
            setShowEditForm(true);
            setShowAddForm(false);
        }
    };

    const handleEditFormSubmit = (data: FieldValues) => {
        handlePatchModel([{ source: data.source }], [modelToEdit!.id.toString()]);
    };

    const handleAddFormSubmit = (data: FieldValues) => {
        handlePostModel(data.source);
    };

    return (
        <StyledSection>
            <StyledH2>Impression Sources</StyledH2>
            <p>Manage the impression sources used in your impression modelling configurations.</p>

            <ConfigTable
                status={isRefreshing ? 'pending' : 'success'}
                isFetching={!!isRefreshing}
                disabled={isDeleting || !!isRefreshing}
                empty={impressionModels.length === 0}
            >
                <ConfigTable.Table maxHeight="200px" emptyMessage="No impression sources found">
                    <ConfigTable.Header>
                        <ConfigTable.Row key="impression-models-header">
                            <ConfigTable.CellHeader />
                            <ConfigTable.CellHeader>Source Name</ConfigTable.CellHeader>
                            <ConfigTable.CellHeader />
                        </ConfigTable.Row>
                    </ConfigTable.Header>
                    <ConfigTable.Body>
                        {impressionModels.map(model => (
                            <ConfigTable.Row key={`impression-model-${model.id}`}>
                                <ConfigTable.CellCheckbox
                                    rowId={model.id}
                                    checked={model.selected}
                                    onCheckedChange={handleCheckboxChange}
                                />
                                <ConfigTable.Cell>{model.value}</ConfigTable.Cell>
                                <ConfigTable.CellActions>
                                    <ConfigTable.ActionDropdownItem
                                        rowId={model.id}
                                        type="edit"
                                        label="Edit"
                                        onClick={handleEditClick}
                                    />
                                    <ConfigTable.ActionDropdownItem
                                        rowId={model.id}
                                        type="delete"
                                        label="Delete"
                                        onClick={handleSingleDeleteClick}
                                    />
                                </ConfigTable.CellActions>
                            </ConfigTable.Row>
                        ))}
                    </ConfigTable.Body>
                </ConfigTable.Table>
                <ConfigTable.ActionBar>
                    <ConfigTable.ActionButton
                        type="delete"
                        label="Delete"
                        onClick={handleBulkDeleteClick}
                        isDisabled={modelsToDelete.length === 0}
                    />
                    <ConfigTable.ActionButton
                        type="add"
                        label="Add Source"
                        onClick={() => {
                            setShowAddForm(true);
                            setShowEditForm(false);
                        }}
                    />
                </ConfigTable.ActionBar>
            </ConfigTable>

            {showEditForm && modelToEdit && (
                <StyledFormContainer>
                    <Form
                        status={isPatching ? 'loading' : 'enabled'}
                        onSubmit={handleEditFormSubmit}
                        defaultValues={{ source: modelToEdit.value }}
                    >
                        <Form.Body>
                            <Form.Section>
                                <Form.SectionTitle>Edit Impression Source</Form.SectionTitle>
                                <Form.Field>
                                    <Form.InputLabel htmlFor="source" label="Source Name" required={true} />
                                    <Form.InputText name="source" validators={[validators.required]} />
                                </Form.Field>
                            </Form.Section>
                        </Form.Body>
                        <Form.Footer>
                            <Form.InputButton value="Apply" type="submit" loading={isPatching} />
                            <Form.InputButton
                                value="Cancel"
                                type="button"
                                buttonTheme={ButtonThemes.Secondary}
                                onClick={() => setShowEditForm(false)}
                            />
                        </Form.Footer>
                    </Form>
                </StyledFormContainer>
            )}

            {showAddForm && (
                <StyledFormContainer>
                    <Form
                        status={isPosting ? 'loading' : 'enabled'}
                        onSubmit={handleAddFormSubmit}
                        defaultValues={{ source: '' }}
                    >
                        <Form.Body>
                            <Form.Section>
                                <Form.SectionTitle>Add Impression Source</Form.SectionTitle>
                                <Form.Field>
                                    <Form.InputLabel htmlFor="source" label="Source Name" required={true} />
                                    <Form.InputText name="source" validators={[validators.required]} />
                                </Form.Field>
                            </Form.Section>
                        </Form.Body>
                        <Form.Footer>
                            <Form.InputButton value="Add" type="submit" loading={isPosting} />
                            <Form.InputButton
                                value="Cancel"
                                type="button"
                                buttonTheme={ButtonThemes.Secondary}
                                onClick={() => setShowAddForm(false)}
                            />
                        </Form.Footer>
                    </Form>
                </StyledFormContainer>
            )}
        </StyledSection>
    );
};

export default LayoutImpressionModels;
